import React, { useState, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Badge, Tooltip, theme as antdTheme, Grid, Drawer, Button } from 'antd';
import type { MenuProps } from 'antd';
import { useAppContext } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import {
	UserOutlined,
	MenuOutlined,
	LogoutOutlined,
	ArrowLeftOutlined,
	DashboardOutlined,
	WalletOutlined,
	BankOutlined,
	AuditOutlined,
	SettingOutlined,
	FileTextOutlined,
	ProfileOutlined,
	BarChartOutlined,
	UnorderedListOutlined,
	ReconciliationOutlined,
	FileDoneOutlined,
	DollarOutlined,
	CreditCardOutlined,
	TransactionOutlined,
} from '@ant-design/icons';

import lightLogo from '../static/cdrpl.png';
import darkLogo from '../static/cdrpl.png';

const { Sider } = Layout;
const { useBreakpoint } = Grid;

interface SidebarProps {
	collapsible: boolean;
	isMobile?: boolean;
	isSmallDesktop?: boolean;
}

const AccountsNavigation = React.memo(() => {
	const screens = useBreakpoint();
	const isMobile = !screens.md;
	const isSmallDesktop = screens.md && !screens.lg;
	return <Sidebar collapsible={!isMobile} isMobile={isMobile} isSmallDesktop={isSmallDesktop} />;
});

const Sidebar = React.memo(({ collapsible, isMobile, isSmallDesktop }: SidebarProps) => {
	const location = useLocation();
	const { state: stateApp, appContextAction } = useAppContext();
	const { isNavMenuClose } = stateApp;
	const { navMenu } = appContextAction;
	const { theme } = useTheme();
	const { user, logout } = useAuth();
	const isDarkMode = theme.algorithm?.[0] === antdTheme.darkAlgorithm;
	const logoPath = isDarkMode ? darkLogo : lightLogo;

	const [drawerVisible, setDrawerVisible] = useState(false);

	const getThemeStyles = useMemo(
		() => ({
			sidebarHeader: {
				padding: isNavMenuClose && !isMobile ? '8px' : '0 16px',
				display: 'flex',
				flexDirection: isNavMenuClose && !isMobile ? ('column' as const) : ('row' as const),
				alignItems: 'center',
				justifyContent: isNavMenuClose && !isMobile ? 'center' : 'space-between',
				overflow: 'hidden',
				transition: 'all 0.3s ease',
				borderBottom: 'none',
				backgroundColor: 'transparent',
			},
			logoContainer: {
				display: 'flex',
				alignItems: 'center',
				paddingBottom: isNavMenuClose && !isMobile ? '8px' : '0',
				justifyContent: isNavMenuClose && !isMobile ? 'center' : 'flex-start',
				marginBottom: isNavMenuClose && !isMobile ? '8px' : '0',
			},
			logo: {
				width: isNavMenuClose && !isMobile ? '50px' : isSmallDesktop ? '180px' : '200px',
				height: 'auto',
				transition: 'width 0.3s ease',
			},
			collapsedProfile: {
				padding: '16px 0',
				display: 'flex',
				justifyContent: 'center',
				borderBottom: 'none',
				marginBottom: '8px',
				backgroundColor: 'transparent',
			},
			navigationMenu: {
				flex: 1,
				overflowY: 'auto' as const,
				overflowX: 'hidden' as const,
				padding: '0',
				height: 'auto',
				minHeight: 0,
			},
			sidebarStyle: {
				boxShadow: 'none',
				zIndex: 1000,
				height: '100vh',
				position: 'fixed' as const,
				left: 0,
				display: 'flex',
				flexDirection: 'column' as const,
				transition: 'all 0.2s ease',
				background: 'transparent',
				backgroundColor: 'transparent',
				overflow: 'hidden',
				...(isSmallDesktop &&
					!isNavMenuClose && {
						width: '220px !important',
					}),
			},
		}),
		[isNavMenuClose, isMobile, isSmallDesktop]
	);

	const currentPath = useMemo(() => {
		const path = location.pathname;
		if (path === '/accounts' || path === '/accounts/') return 'dashboard';
		if (path === '/accounts/dashboard') return 'dashboard';
		if (path.startsWith('/accounts/reimbursements')) return 'reimbursements';
		if (path.startsWith('/accounts/gst-payment')) return 'gst-payment';
		if (path.startsWith('/accounts/tds-payment')) return 'tds-payment';
		if (path.startsWith('/accounts/payment-reports')) return 'payment-reports';
		if (path.startsWith('/accounts/work-orders')) return 'work-orders';
		if (path.startsWith('/accounts/settings')) return 'settings';
		if (path.startsWith('/accounts/financial/chart-of-accounts')) return 'financial-chart-of-accounts';
		if (path.startsWith('/accounts/financial/ledger')) return 'financial-ledger';
		if (path.startsWith('/accounts/financial/invoices')) return 'financial-invoices';
		if (path.startsWith('/accounts/financial/outstanding')) return 'financial-outstanding';
		if (path.startsWith('/accounts/financial/transactions')) return 'financial-transactions';
		if (path.startsWith('/accounts/payment-entry')) return 'payment-entry';
		return 'dashboard';
	}, [location.pathname]);

	const menuItems: MenuProps['items'] = useMemo(() => {
		const items: MenuProps['items'] = [];

		//if (user?.isadmin || canReadAccountsDashboard) {
			items.push({
				key: 'dashboard',
				icon: <DashboardOutlined />,
				label: <Link to="/accounts/dashboard">Dashboard</Link>,
			});
		// }

		// if (user?.isadmin || canReadAccountsReimbursements) {
			items.push({
				key: 'reimbursements',
				icon: <WalletOutlined />,
				label: <Link to="/accounts/reimbursements">Reimbursements</Link>,
			});
		// }

		// if (user?.isadmin || canReadAccountsGSTPayment) {
			items.push({
				key: 'gst-payment',
				icon: <BankOutlined />,
				label: <Link to="/accounts/gst-payment">GST Payment</Link>,
			});
		// }

		// if (user?.isadmin || canReadAccountsTDSPayment) {
			items.push({
				key: 'tds-payment',
				icon: <AuditOutlined />,
				label: <Link to="/accounts/tds-payment">TDS Payment</Link>,
			});
		// }

		// if (user?.isadmin || canReadAccountsPaymentReports) {
			items.push({
				key: 'payment-reports',
				icon: <FileTextOutlined />,
				label: <Link to="/accounts/payment-reports">Payment Reports</Link>,
			});
		// }

		// if (user?.isadmin || canReadAccountsWorkOrders) {
			items.push({
				key: 'work-orders',
				icon: <ProfileOutlined />,
				label: <Link to="/accounts/work-orders">Work Orders</Link>,
			});
		// }

			items.push({
				key: 'payment-entry',
				icon: <CreditCardOutlined />,
				label: <Link to="/accounts/payment-entry">Payment Entry</Link>,
			});



		const financialChildren: MenuProps['items'] = [];
		// if (user?.isadmin || canReadFinancialChartOfAccounts) {
			financialChildren.push({
				key: 'financial-chart-of-accounts',
				icon: <UnorderedListOutlined />,
				label: <Link to="/accounts/financial/chart-of-accounts">Chart of Accounts</Link>,
			});
		// }
		// if (user?.isadmin || canReadFinancialLedger) {
			financialChildren.push({
				key: 'financial-ledger',
				icon: <ReconciliationOutlined />,
				label: <Link to="/accounts/financial/ledger">General Ledger</Link>,
			});
		// }
		// if (user?.isadmin || canReadFinancialInvoices) {
			financialChildren.push({
				key: 'financial-invoices',
				icon: <FileDoneOutlined />,
				label: <Link to="/accounts/financial/invoices">Invoice History</Link>,
			});
		// }
		// if (user?.isadmin || canReadFinancialOutstanding) {
			financialChildren.push({
				key: 'financial-outstanding',
				icon: <DollarOutlined />,
				label: <Link to="/accounts/financial/outstanding">Outstanding Bills</Link>,
			});
		// }
			financialChildren.push({
				key: 'financial-transactions',
				icon: <TransactionOutlined />,
				label: <Link to="/accounts/financial/transactions">Transactions</Link>,
			});

		if (financialChildren.length > 0) {
			items.push({
				key: 'financial-data',
				icon: <BarChartOutlined />,
				label: 'Financial Data',
				children: financialChildren,
			});
		}

		// if (user?.isadmin || canReadAccountsSettings) {
			items.push({
				key: 'settings',
				icon: <SettingOutlined />,
				label: <Link to="/accounts/settings">Settings</Link>,
			});
		// }

		items.push({ type: 'divider' });
		items.push({
			key: 'back-to-stages',
			icon: <ArrowLeftOutlined />,
			label: <Link to="/project-selection">Back to Previous</Link>,
		});
		items.push({
			key: 'logout',
			icon: <LogoutOutlined />,
			label: 'Logout',
			onClick: logout,
			danger: true,
		});

		return items;
	}, [
		logout
	]);

	const toggleCollapse = useCallback(() => {
		if (isNavMenuClose) {
			navMenu.expand();
		} else {
			navMenu.collapse();
		}
	}, [isNavMenuClose, navMenu]);

	const handleMenuClick = useCallback(
		(info: any) => {
			if (info.key === 'logout') {
				logout();
			}

			if (isMobile) {
				setDrawerVisible(false);
			}
		},
		[logout, isMobile]
	);

	const toggleDrawer = useCallback(() => {
		setDrawerVisible((prev) => !prev);
	}, []);

	const SidebarContent = useMemo(
		() => (
			<div
				className="transparent-nav"
				style={{
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				}}
			>
				<div className="sidebar-header" style={getThemeStyles.sidebarHeader}>
					<div className="logo-container" style={getThemeStyles.logoContainer}>
						<img src={logoPath} alt="CDRPL" style={getThemeStyles.logo} />
					</div>
				</div>

				{isNavMenuClose && !isMobile && (
					<div style={getThemeStyles.collapsedProfile}>
						<Tooltip title={user?.name || 'User'} placement="right">
							<Badge dot status="success" offset={[-4, 28]}>
								<Avatar icon={<UserOutlined />} size={36} />
							</Badge>
						</Tooltip>
					</div>
				)}

				<div className="navigation-menu" style={getThemeStyles.navigationMenu}>
					<Menu
						mode="inline"
						selectedKeys={[currentPath]}
						defaultOpenKeys={currentPath.startsWith('financial-') ? ['financial-data'] : []}
						items={menuItems}
						onClick={handleMenuClick}
						style={{
							border: 'none',
							background: 'transparent',
							height: '100%',
						}}
						theme={isDarkMode ? 'dark' : 'light'}
					/>
				</div>
			</div>
		),
		[
			getThemeStyles,
			logoPath,
			isNavMenuClose,
			isMobile,
			user,
			currentPath,
			menuItems,
			handleMenuClick,
			isDarkMode,
		]
	);

	if (isMobile) {
		return (
			<>
				<Button
					type="primary"
					shape="circle"
					icon={<MenuOutlined />}
					onClick={toggleDrawer}
					style={{
						position: 'fixed',
						top: '16px',
						left: '16px',
						zIndex: 999,
						boxShadow: '0 3px 6px rgba(0,0,0,0.2)',
					}}
					size="middle"
				/>
				<Drawer
					placement="left"
					onClose={toggleDrawer}
					open={drawerVisible}
					width={320}
					styles={{
						body: {
							padding: 0,
							height: '100%',
							background: 'transparent',
							overflow: 'hidden',
							display: 'flex',
							flexDirection: 'column',
						},
					}}
					closable={false}
				>
					{SidebarContent}
				</Drawer>
			</>
		);
	}

	return (
		<Sider
			collapsible={collapsible}
			collapsed={isNavMenuClose}
			onCollapse={toggleCollapse}
			className="navigation-sidebar transparent-nav"
			theme={isDarkMode ? 'dark' : 'light'}
			width={isSmallDesktop ? 220 : 260}
			collapsedWidth={80}
			style={getThemeStyles.sidebarStyle}
			trigger={null}
		>
			{SidebarContent}
		</Sider>
	);
});

export default AccountsNavigation;
